/**
 * Hooks React Query pra leitura de billing **da própria clinic do
 * owner/admin** (não confundir com `features/plans-admin/` que é
 * catálogo de planos do superadmin, ou `features/clinics/` que é
 * leitura cross-clinic do superadmin).
 *
 * Aqui o `clinic_id` SEMPRE vem do JWT via `useCurrentClinicId()` —
 * RLS no banco enforça que owner só vê dados da própria clinic.
 *
 * Casos de uso (PR-M2-00 e adiante):
 *   - `PaymentPendingPage` (tela de gate Modelo C híbrido) — mostra
 *     plano, próxima cobrança e link de pagamento Asaas
 *   - `PlanPage` futuro (PR-M2-02) — histórico de faturas, status do
 *     plano, link pra trocar método de pagamento
 *   - Banner de trial/inadimplência em `DashboardPage` futuro
 */
import { useQuery } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';

export const CLINIC_BILLING_KEYS = {
  ownSubscription: (clinicId: string) => ['clinic-billing', 'own-subscription', clinicId] as const,
  ownNextInvoice: (clinicId: string) => ['clinic-billing', 'own-next-invoice', clinicId] as const,
  ownInvoices: (clinicId: string, page: number, pageSize: number) =>
    ['clinic-billing', 'own-invoices', clinicId, { page, pageSize }] as const,
};

/**
 * Subscription ativa do owner (via JWT clinic_id) + plan embedado.
 * RLS: `subscriptions_select_member` + `plans_select_*` autorizam.
 *
 * `refetchInterval` opcional ativa polling — usado em
 * `PaymentPendingPage` pra detectar transição `'incomplete' →
 * 'active'` automaticamente quando webhook Asaas processa primeira
 * fatura (sem owner precisar dar F5).
 */
export interface OwnSubscriptionRow {
  id: string;
  clinic_id: string;
  plan_id: string;
  // Enum exato do CHECK em migration 0007 — NÃO inclui 'unpaid'.
  // Removido 'unpaid' em 2026-04-26 (drift que vinha de Asaas terminology;
  // CHECK do banco rejeita silenciosamente).
  status: 'incomplete' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'paused';
  billing_cycle: 'monthly' | 'yearly' | string;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  asaas_customer_id: string | null;
  asaas_subscription_id: string | null;
  created_at: string;
}

export interface OwnPlanRow {
  id: string;
  key: string;
  name_pt_br: string;
  monthly_price_brl: number | null;
  yearly_price_brl: number | null;
  billing_cycle: string | null;
  tier: string | null;
}

export interface OwnSubscriptionWithPlan {
  subscription: OwnSubscriptionRow;
  plan: OwnPlanRow | null;
}

export function useOwnSubscription(
  clinicId: string | null | undefined,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: CLINIC_BILLING_KEYS.ownSubscription(clinicId ?? ''),
    enabled: Boolean(clinicId),
    staleTime: 10_000,
    refetchInterval: options?.refetchInterval ?? false,
    queryFn: async (): Promise<OwnSubscriptionWithPlan | null> => {
      if (!clinicId) return null;
      const subQ = await supabase
        .from('subscriptions')
        .select(
          'id, clinic_id, plan_id, status, billing_cycle, trial_ends_at, current_period_start, current_period_end, asaas_customer_id, asaas_subscription_id, created_at',
        )
        .eq('clinic_id', clinicId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1);
      if (subQ.error) throw new Error(subQ.error.message);
      const sub = (subQ.data ?? [])[0] as OwnSubscriptionRow | undefined;
      if (!sub) return null;

      const planQ = await supabase
        .from('plans')
        .select('id, key, name_pt_br, monthly_price_brl, yearly_price_brl, billing_cycle, tier')
        .eq('id', sub.plan_id)
        .maybeSingle();
      if (planQ.error) throw new Error(planQ.error.message);

      return {
        subscription: sub,
        plan: (planQ.data as OwnPlanRow | null) ?? null,
      };
    },
  });
}

/**
 * Próxima fatura pendente da clinic (status `pending`/`overdue`,
 * `due_date` mais antigo). Usada na PaymentPendingPage pra mostrar o
 * boleto/PIX que precisa pagar pra liberar a plataforma.
 *
 * RLS: `invoices_select_member` autoriza owner ler próprias.
 */
export interface OwnInvoiceRow {
  id: string;
  clinic_id: string;
  subscription_id: string | null;
  asaas_payment_id: string | null;
  amount_brl: number;
  // Enum exato do CHECK em migration 0007 — DB usa terminologia Asaas
  // (`'confirmed'` = pagamento detectado, `'received'` = compensado).
  // Antes 2026-04-26 tipava `'paid'` (terminologia genérica), que NÃO
  // existe no CHECK e quebrava badges/links na UI (`status === 'paid'`
  // nunca casava).
  status: 'pending' | 'confirmed' | 'received' | 'overdue' | 'canceled' | 'refunded' | string;
  due_date: string;
  paid_at: string | null;
  payment_method: string | null;
  invoice_url: string | null;
  pdf_url: string | null;
  created_at: string;
}

export function useOwnNextInvoice(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: CLINIC_BILLING_KEYS.ownNextInvoice(clinicId ?? ''),
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    queryFn: async (): Promise<OwnInvoiceRow | null> => {
      if (!clinicId) return null;
      const { data, error } = await supabase
        .from('invoices')
        .select(
          'id, clinic_id, subscription_id, asaas_payment_id, amount_brl, status, due_date, paid_at, payment_method, invoice_url, pdf_url, created_at',
        )
        .eq('clinic_id', clinicId)
        .in('status', ['pending', 'overdue'])
        .order('due_date', { ascending: true })
        .limit(1);
      if (error) throw new Error(error.message);
      return ((data ?? [])[0] as OwnInvoiceRow | undefined) ?? null;
    },
  });
}

/**
 * Lista paginada de TODAS as faturas da clinic (independente de status).
 * Ordenada por `due_date desc` — mais recentes no topo. Usada na
 * `PlanPage` (PR-M2-02) pra histórico financeiro completo.
 *
 * Inclui faturas de:
 *   - **Subscription SaaS** — `subscription_id IS NOT NULL` (recurring
 *     mensal/anual via `asaas_subscription_id`)
 *   - **Parcelamento de consultoria** — `subscription_id IS NULL` mas
 *     `asaas_payment_id IS NOT NULL` (parcelamento Asaas finito,
 *     populado pelo `asaas-webhook` quando webhook traz cada parcela
 *     da consultoria contratada)
 *
 * UI distingue visualmente os 2 tipos via badge ("Plataforma" vs
 * "Consultoria") com base em `subscription_id IS NULL`.
 *
 * Retorna `{ rows, total, hasMore }` pra UI fazer "Carregar mais"
 * incremental sem reload total.
 */
export interface OwnInvoicesPage {
  rows: OwnInvoiceRow[];
  total: number;
  hasMore: boolean;
}

export function useOwnInvoices(
  clinicId: string | null | undefined,
  options?: { page?: number; pageSize?: number },
) {
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? 12;
  return useQuery({
    queryKey: CLINIC_BILLING_KEYS.ownInvoices(clinicId ?? '', page, pageSize),
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    queryFn: async (): Promise<OwnInvoicesPage> => {
      if (!clinicId) return { rows: [], total: 0, hasMore: false };
      // PostgREST: `count: 'exact'` retorna `count` no header — usado
      // pra calcular `hasMore` (page * size < total).
      const from = page * pageSize;
      const to = from + pageSize - 1;
      const { data, count, error } = await supabase
        .from('invoices')
        .select(
          'id, clinic_id, subscription_id, asaas_payment_id, amount_brl, status, due_date, paid_at, payment_method, invoice_url, pdf_url, created_at',
          { count: 'exact' },
        )
        .eq('clinic_id', clinicId)
        .order('due_date', { ascending: false })
        .range(from, to);
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as OwnInvoiceRow[];
      const total = count ?? rows.length;
      return {
        rows,
        total,
        hasMore: from + rows.length < total,
      };
    },
  });
}
